import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useOverrideIdContext } from 'contexts/OverrideIdContext';

import useQueryParams from '@polyai/common/hooks/useQueryParams';

import OTError from 'components/organisms/OTError/OTError';
import OTFromScreen from 'components/organisms/OTFormScreen/OTFromScreen';
import OTStatusScreen from 'components/organisms/OTStatusScreen/OTStatusScreen';
import { useUpdateOpenTableIntegration } from 'hooks/api/opentable';
import { usePendo } from 'hooks/usePendo';
import useProjects from 'hooks/useProjects';
import { buildRedirectUrl } from 'lib/opentable/callback';
import type { Region } from 'screens/OpenTableRegion/types';
import {
  AgentForm,
  AgentVoiceForm,
  TimezoneForm,
  WebsiteUrlForm,
} from './components';
import { BACK_STEP_MAP, DEFAULT_OPEN_TABLE_AGENT_CONFIG } from './constants';
import type {
  AgentFormData,
  AgentSetupStep,
  AgentVoiceFormData,
  OpenTableAgentConfig,
  ParsedState,
  RequestParam,
  RetryCtx,
  TimezoneFormData,
  UiStage,
  WebsiteUrlFormData,
} from './types';

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: existing
const OpenTableNewAgent: React.FC = () => {
  const [uiStage, setUiStage] = useState<UiStage>('getting_projects');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [retryCtx, setRetryCtx] = useState<RetryCtx | null>(null);
  const [currentStep, setCurrentStep] =
    useState<AgentSetupStep>('selectTimezone');
  const [agentConfig, setAgentConfig] = useState<OpenTableAgentConfig>(
    DEFAULT_OPEN_TABLE_AGENT_CONFIG,
  );

  const searchParams = useQueryParams();

  const { projects, isLoadingProjects, isProjectsError, refetchProjects } =
    useProjects();

  // Extract query parameters
  const redirectUri = searchParams.get('redirect_uri') ?? '';
  const stateParam = searchParams.get('state') ?? '';
  const code = searchParams.get('code') ?? '';
  const workspaceId = searchParams.get('workspace_id') ?? '';
  const restaurantRegion = (searchParams.get('restaurant_region') ??
    '') as Region;
  const companyName = searchParams.get('company_name') ?? 'OpenTable Agent';

  // Initialize Pendo for tracking
  usePendo({
    accountId: workspaceId || undefined,
    projectId: agentConfig.projectId ?? undefined,
  });

  // Parse state JSON parameter
  const { rid }: { rid: string | null } = useMemo(() => {
    let rid: string | null = null;

    try {
      if (stateParam) {
        const parsedState: ParsedState = JSON.parse(stateParam);
        rid = parsedState?.rid != null ? String(parsedState.rid) : null;
      }
    } catch (e) {
      // non-fatal; handled via missing params UI
      // eslint-disable-next-line no-console
      console.error('Failed to parse state parameter:', e);
    }
    return { rid };
  }, [stateParam]);

  const {
    mutate: updateIntegration,
    isLoading: isUpdatingIntegration,
    isSuccess: isUpdatingIntegrationSuccess,
    isError: isUpdatingIntegrationError,
  } = useUpdateOpenTableIntegration();

  const {
    accountId: overriddenAccountId,
    setAccountId,
    reset: resetIdOverrides,
  } = useOverrideIdContext();

  const handleUpdateIntegration = useCallback(() => {
    setUiStage('linking'); // keep showing "Connecting..." while linking
    updateIntegration({
      account_id: workspaceId!,
      restaurant_id: rid!,
      project_id: agentConfig.projectId,
      additional_information: {
        voice_id: agentConfig.voiceId,
        agent_name: agentConfig.agentName,
        timezone: agentConfig.timezone,
        website_url: agentConfig.websiteUrl,
      },
    });
  }, [agentConfig, rid, updateIntegration, workspaceId]);

  const handleOpenTableRedirection = useCallback(() => {
    setUiStage('redirecting'); // keep showing "Connecting..." while navigating
    const redirect = buildRedirectUrl(
      code,
      stateParam,
      redirectUri,
      redirectUri,
    );
    if (redirect.success) {
      window.location.replace(redirect.url);
    } else {
      setRetryCtx({
        step: 'redirecting',
      });
      throw new Error(redirect.error);
    }
  }, [code, redirectUri, stateParam]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Deliberate
  useEffect(() => {
    if (!workspaceId || !rid) {
      setErrorMsg('Could not find restaurant ID or workspace ID.');
    } else if (workspaceId !== overriddenAccountId) {
      setAccountId(workspaceId);
    }
  }, [workspaceId, rid]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Deliberate
  useEffect(() => {
    if (!workspaceId || !rid) {
      return;
    }

    if (agentConfig.projectId || agentConfig.isNewProject) {
      setUiStage('linking');
      setRetryCtx({ step: 'updateIntegration' });
      handleUpdateIntegration();
    }
  }, [agentConfig.projectId, agentConfig.isNewProject]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Deliberate
  useEffect(() => {
    try {
      if (isUpdatingIntegrationSuccess) {
        resetIdOverrides();
        handleOpenTableRedirection();
      } else if (isUpdatingIntegrationError) {
        throw new Error('Failed to link agent');
      }
    } catch (error: any) {
      setUiStage('idle');
      setErrorMsg(error?.message || 'Something went wrong.');
    }
  }, [isUpdatingIntegration]);

  useEffect(() => {
    return () => {
      resetIdOverrides();
    };
  }, [resetIdOverrides]);

  useEffect(() => {
    if (!isLoadingProjects) {
      if (isProjectsError) {
        setErrorMsg('Failed to load agents. Please try again.');
        setRetryCtx({ step: 'gettingProjects' });
      } else {
        setUiStage('idle');
      }
    }
  }, [isLoadingProjects, isProjectsError]);

  // Validate that required query parameters are present
  const missingParams = useMemo(() => {
    const req: RequestParam[] = [
      { name: 'redirect_uri', value: redirectUri },
      { name: 'state', value: stateParam },
      { name: 'code', value: code },
    ].filter(p => !p.value);

    if (stateParam) {
      if (!rid) {
        req.push({ name: 'rid (in state)', value: null });
      }
      if (!workspaceId) {
        req.push({ name: 'workspaceId (in state)', value: null });
      }
    }
    return req;
  }, [redirectUri, stateParam, code, rid, workspaceId]);

  // ---------- ERROR UI WITH SMART RETRY ----------
  const handleRetry = async () => {
    if (!retryCtx) {
      return;
    }

    try {
      setErrorMsg(null);
      switch (retryCtx.step) {
        case 'updateIntegration':
          handleUpdateIntegration();
          break;

        case 'redirecting':
          handleOpenTableRedirection();
          break;

        case 'gettingProjects':
          await refetchProjects();
          setUiStage('getting_projects');
          break;
      }
    } catch (error: any) {
      setUiStage('idle');
      setErrorMsg(error?.message || 'Something went wrong.');
    }
  };

  // ---------- EARLY ERROR STATES ----------
  if (missingParams.length > 0) {
    return (
      <OTError
        description={`The following query parameter${
          missingParams.length > 1 ? 's are' : ' is'
        } missing: ${missingParams.map(p => p.name).join(', ')}`}
        retryLabel="Try Again"
        title="Missing Required Information"
      />
    );
  }

  if (errorMsg) {
    return (
      <OTError
        description={errorMsg}
        retryLabel="Retry"
        title="Linking failed"
        onRetry={handleRetry}
      />
    );
  }

  // ---------- LOADING UI (persists during redirect) ----------
  if (uiStage !== 'idle' || isUpdatingIntegration) {
    const status =
      uiStage === 'getting_projects'
        ? 'Loading agents…'
        : uiStage === 'creating'
          ? 'Preparing your workspace…'
          : 'Setting up your agent…';
    return <OTStatusScreen status={status} />;
  }

  const onSubmitTimezone = (data: TimezoneFormData) => {
    setAgentConfig({
      ...agentConfig,
      timezone: data.timezone.value,
    });
    setCurrentStep('websiteUrl');
  };

  const onSubmitWebsiteUrl = (data: WebsiteUrlFormData) => {
    setAgentConfig({
      ...agentConfig,
      websiteUrl: data.websiteUrl,
    });
    setCurrentStep(projects.length > 0 ? 'selectAgent' : 'selectVoice');
  };

  const onSubmitAgent = (data: AgentFormData) => {
    setAgentConfig({
      ...agentConfig,
      projectId: data.agent!.value,
    });
  };

  const onSubmitAgentVoice = (data: AgentVoiceFormData) => {
    setAgentConfig({
      ...agentConfig,
      voiceId: data.voice?.value,
      isNewProject: true,
      agentName: companyName,
    });
  };

  // ---------- BACK BUTTON LOGIC ----------
  const goBack = () => {
    setCurrentStep(BACK_STEP_MAP[currentStep]!);
  };

  let form: React.ReactNode = null;
  switch (currentStep) {
    case 'selectTimezone':
      form = (
        <TimezoneForm
          isSubmitting={false}
          timezone={agentConfig.timezone}
          onSubmit={onSubmitTimezone}
          region={restaurantRegion}
        />
      );
      break;
    case 'websiteUrl':
      form = (
        <WebsiteUrlForm
          isSubmitting={false}
          websiteUrl={agentConfig.websiteUrl}
          onSubmit={onSubmitWebsiteUrl}
        />
      );
      break;
    case 'selectAgent':
      form = (
        <AgentForm
          isLoadingProjects={isLoadingProjects}
          isSubmitting={false}
          projects={projects}
          onSubmit={onSubmitAgent}
        />
      );
      break;
    case 'selectVoice':
      form = (
        <AgentVoiceForm
          isSubmitting={false}
          onSubmit={onSubmitAgentVoice}
          region={restaurantRegion}
        />
      );
      break;
  }

  const skipTimezoneStep =
    restaurantRegion === 'united-kingdom' || restaurantRegion === 'ireland';
  const canGoBack =
    BACK_STEP_MAP[currentStep] !== null &&
    (currentStep !== 'websiteUrl' || !skipTimezoneStep);

  // ---------- FORM UI ----------
  return (
    <OTFromScreen canGoBack={canGoBack} onBack={goBack}>
      {form}
    </OTFromScreen>
  );
};

export default OpenTableNewAgent;
